import type { CartState } from "@ai-guidance/shared";

/**
 * Platformele pentru care avem un adaptor: Shopify (Ajax Cart API) și
 * WooCommerce (Store API). Uniunea e închisă în ambele capete - backendul
 * validează la servire, SDK-ul la citire.
 */
export type CommercePlatform = "shopify" | "woocommerce";

/**
 * Ce primim din configul public al appului, DOAR pe appuri commerce
 * (docs/plan-commerce-actions.md §5). Absent = situl nu e magazin, iar SDK-ul se
 * comportă exact ca azi: niciun adaptor, niciun card, niciun `cart` în snapshot.
 *
 * Câmpul nu trece prin `runtimeConfigResponseSchema` (zod scoate cheile
 * necunoscute), deci îl citim din JSON-ul brut - vezi `readCommerceConfig` și
 * `RuntimeApi.fetchConfig`.
 */
export interface CommerceConfig {
  platform: CommercePlatform;
  /** Unde duce `open_checkout`. Din config, nu din model - vezi `CommerceAdapter.cartPath`. */
  cartPath: string;
}

/**
 * Calea implicită a coșului, când backendul n-a trimis una utilizabilă.
 * Oglindește `DEFAULT_CART_PATH` din backend (services/commerceActions.ts) -
 * duplicat deliberat: SDK-ul trebuie să rămână corect și în fața unui backend
 * mai vechi, care nu știe de platforme.
 */
const DEFAULT_CART_PATH: Record<CommercePlatform, string> = {
  shopify: "/cart",
  woocommerce: "/cart/",
};

/**
 * Rezultatul unei acțiuni de coș. Adaptorul NU aruncă niciodată nechemat: o
 * rețea picată, un 422 „sold out" sau un JSON stricat ies pe aceeași ușă, cu un
 * mesaj scurt pe care îl vede vizitatorul (toast) și îl vede agentul în tura
 * următoare (`snapshot.lastActionError`).
 */
export type AdapterResult = { ok: true; sections?: Record<string, string> } | { ok: false; message: string };

/**
 * Ce poate cere runtime-ul de la mutație PE LÂNGĂ mutația însăși.
 *
 * Deocamdată doar re-randarea secțiunilor temei în ACELAȘI răspuns (Shopify:
 * `sections` + `sections_url` pe /cart/add.js și /cart/change.js) - un drum
 * dus-întors în loc de două. Opțional peste tot: un adaptor care nu știe de
 * secțiuni îl ignoră, iar `themeSync` cade pe fallback-ul temei.
 */
export interface MutationContext {
  /** Id-urile de secțiune citite din DOM (`themeSync.cartSectionIds`). Gol = nu cerem nimic. */
  sections?: string[];
  /** Calea față de care se randează secțiunile; Shopify o cere ca `sections_url`. */
  sectionsUrl?: string;
}

/**
 * Contractul unei platforme de magazin. Shopify a fost prima implementare
 * (`createShopifyAdapter`), WooCommerce a doua (`createWooCommerceAdapter`) - și
 * a confirmat ce promitea interfața: executorul, cardurile, snapshotul și
 * promptul n-au fost atinse deloc.
 *
 * `variantId` e string peste tot - așa vine din catalog și din acțiunile
 * modelului; fiecare adaptor îl traduce în ce cere API-ul lui (pentru Shopify:
 * număr, vezi shopify.ts; pentru Woo: număr la adăugare, CHEIE DE LINIE la
 * modificare, vezi woocommerce.ts).
 */
export interface CommerceAdapter {
  /**
   * Platforma pe care o vorbește adaptorul. Singurul consumator e `themeSync`,
   * care trebuie să știe în ce limbă să-i spună temei că i-am schimbat coșul -
   * un eveniment Shopify pe o temă WordPress n-ar face nimic, și invers.
   */
  readonly platform: CommercePlatform;
  addToCart(variantId: string, quantity: number, ctx?: MutationContext): Promise<AdapterResult>;
  setQuantity(variantId: string, quantity: number, ctx?: MutationContext): Promise<AdapterResult>;
  /** `null` = coșul n-a putut fi citit acum (rețea, 404 pe un sit care nu e magazin). */
  getCart(): Promise<CartState | null>;
  cartPath(): string;
  /**
   * Calea paginii de rezultate a magazinului pentru `query` (`open_search`).
   *
   * URL-ul îl compune ADAPTORUL, nu modelul: modelul dă doar textul căutat, deci
   * nu există cale prin care să trimită vizitatorul altundeva - encodarea și
   * forma rutei sunt ale platformei. Alt magazin, altă rută (Shopify:
   * `/search?q=`), iar executorul rămâne același.
   *
   * `bounds` e bugetul rostit de vizitator, în monedă întreagă (lei). Tot
   * adaptorul îl traduce în filtrul platformei - pe Shopify sunt parametri de
   * URL (`filter.v.price.gte`/`lte`), pe alta ar putea fi altceva.
   */
  searchPath(query: string, bounds?: PriceBounds): string;
  /**
   * Câte produse se văd ACUM, dacă pagina curentă e o pagină de rezultate a
   * platformei. `undefined` = nu suntem pe o astfel de pagină SAU nu putem
   * spune cu încredere - niciodată un `0` de complezență (vezi shopify.ts).
   *
   * Opțională pe interfață: un adaptor viitor care nu știe să citească grila
   * temei lui rămâne valid și pur și simplu nu urcă semnalul.
   */
  searchResults?(): SearchResults | undefined;
}

/**
 * Câte produse sunt PE ECRAN pe pagina de rezultate a temei, citite structural.
 *
 * Există fiindcă `open_search` predă pagina temei, iar agentul reia conversația
 * acolo fără niciun semnal despre ce a ieșit: pe zero rezultate ghicea din text
 * și ori se bâlbâia, ori descria entuziast o listă goală.
 */
export interface SearchResults {
  /** Interogarea, așa cum e în URL (`?q=`). Absentă pe o colecție deschisă direct. */
  query?: string;
  /** Numărul de produse distincte randate în grilă. 0 = grila e goală, verificat, nu presupus. */
  count: number;
}

/** Bugetul rostit de vizitator, în monedă întreagă. Ambele capete opționale. */
export interface PriceBounds {
  minPrice?: number | undefined;
  maxPrice?: number | undefined;
}

/**
 * Citire DEFENSIVĂ a câmpului `commerce` din /config: orice formă neașteptată
 * (backend vechi, platformă pe care n-o știm încă, cartPath absolut) întoarce
 * `null`, adică „nu e magazin" - starea de azi, nu o eroare.
 */
export function readCommerceConfig(raw: unknown): CommerceConfig | null {
  if (!raw || typeof raw !== "object") return null;
  const value = raw as { platform?: unknown; cartPath?: unknown };
  // Lista e închisă: o platformă pe care SDK-ul ăsta n-o cunoaște (backend mai
  // nou decât scriptul din cache-ul browserului) înseamnă „nu e magazin", adică
  // exact comportamentul de dinaintea feature-ului - niciodată un adaptor greșit.
  const platform: CommercePlatform | null =
    value.platform === "shopify" || value.platform === "woocommerce" ? value.platform : null;
  if (!platform) return null;
  const path = typeof value.cartPath === "string" ? value.cartPath : "";
  // Same-origin, ca `navigate`: o cale absolută (sau protocol-relativă) ar
  // însemna că `open_checkout` poate scoate vizitatorul de pe situl clientului.
  const cartPath = path.startsWith("/") && !path.startsWith("//") ? path : DEFAULT_CART_PATH[platform];
  return { platform, cartPath };
}
